import React from 'react';
import Immutable from 'immutable';
import {
	DateUtils,
	Icon,
	REPORTED_EXCEPTION,
	styled,
	webapi
} from 'ps-gui';
import FileList from '../components/filelist';
import {
	getFormItemLabel,
	getPhrase
} from '../data/phrases';
import { getLookup } from '../data/lookup';
import {
	Dialog,
	DialogContent,
	DialogHeader
} from '../components/dialog';
import { downloadFile } from './utils';
import DiffMatchPatch from 'diff-match-patch';
import memoizeOne from 'memoize-one';

const EMPTY_LIST = Immutable.List([]);
const EMPTY_MAP = Immutable.Map({});

export const Label = styled.div`
	font-weight: 500;
	font-size: 14px;
	line-height: 30px;
	color: #575757;

	.hint {
		margin-left: 16px;
		font-size: 14px;
		font-weight: 400;
	}
`;

export const Buttons = styled.div`
	.ps-button {
		margin-right: 24px;
	}
`;

export const StatusBubble = styled.span`
	display: inline-block;
	font-size: 14px;
	font-weight: 400;
	padding: 2px 12px;
	border-radius: 16px;

	margin-left: ${props => props.marginLeft};
	&.CANCELLED {
		color: #575757;
		background: #F2F2F2;
	}
	&.COMPLETED {
		color: #3EAC86;
		background: #E0F6EE;
	}
	&.DENIED {
		color: #DC0000;
		background: #FCE5E5;
	}
	&.IN_PROGRESS {
		color: #F07431;
		background: #FEF1EA;
	}
	&.RETURNED {
		color: #F07431;
		background: #FEF1EA;
	}
	&.SAVED_FOR_LATER {
		color: #575757;
		background: #F2F2F2;
	}
	&.NEW {
		color: #575757;
		background: #F2F2F2;
	}
	&.SUBMITTED {
		color: #575757;
		background: #F2F2F2;
	}

`;

const ReadOnlyDetailWrapper = styled.div`
	max-width: 700px;

	.value {
		font-weight: 400;
		font-size: 16px;
		line-height: 30px;
		color: #575757;
		margin-bottom: 24px;
		white-space: pre-wrap;
	}
	.party {
		font-weight: 700;
		font-size: 20px;
		line-height: 36px;
		color: #306DA0;
		.partyName {
			color: #306DA0;
		}
		.roleType {
			margin-left: 8px;
			color2: #575757;
		}
		.partyId {
			margin-left: 8px;
			font-weight: 500;
			user-select: all;
			color: #575757;
			font-size: 18px;
		}
		margin-bottom: 24px;
		margin-top: 24px;
	}
	.name {
		font-weight: 900;
		line-height: 36px;
		font-size: 20px;
		color: #575757;
	}
	.typeAndStatus {
		border-bottom: 1px solid #C6DAE7;
		padding-bottom: 18px;
		margin-bottom: 40px;
		.type {
			color: #306DA0;
			font-size: 18px;
			font-weight: 500;
			line-height: 30px;
			margin-right: 16px;
		}
	}
`;

export class ReadOnlyDetail extends React.PureComponent {
	renderFiles(files) {
		return (
			<FileList
				files={files}
				onDownloadRequested={this.props.onFileDownloadRequested}
			/>
		);
	}

	render() {
		const detail = this.props.detail;
		return (
			<ReadOnlyDetailWrapper>
				<div className="party">
					<span className="partyName">{detail.get('partyName')}</span>
					<span className="roleType">({detail.get('roleType')})</span>
					{
						this.props.withUser && <span className='partyId'>{detail.get('partyId')}</span>
					}
				</div>
				<div className="name">
					{detail.get('name')}
				</div>
				<div className="typeAndStatus">
					<span className="type">{detail.get('type')}</span>
					<StatusBubble className={detail.get('statusId')}>
						{detail.get('status')}
					</StatusBubble>
				</div>
				<Label>{getFormItemLabel('created_when', 'submission')}</Label>
				<div className="value">
					{DateUtils.formatDate(detail.get('createdWhen'), false)}
					{
						!!this.props.withUser &&
							<span> - {detail.get('userDisplayName')} ({detail.get('userName')})</span>

					}
				</div>
				<Label>{getFormItemLabel('comments', 'submission')}</Label>
				<div className="value">{detail.get('comments')}</div>
				{this.renderFiles(detail.get('files'))}
				<Buttons>
					{
						this.props.buttons
					}
				</Buttons>
			</ReadOnlyDetailWrapper>
		);
	}
}

const StatusChangeHistoryWrapper = styled.div`
	padding: 16px;
	background: #F3FAFD;
	border-radius: 15px;
	width: 370px;
	margin: 40px auto;
	display: table;

	.header {
		color: #575757;
		font-size: 18px;
		line-height: 30px;
		font-weight: 500;
		margin-bottom: 16px;
	}

	.transition {
		margin-bottom: 24px;
		&.active {
			color: #575757;
			cursor: pointer;
			&:hover {
				.createdWhen, .statusText {
					text-decoration: underline;
				}
				.more {
					background-color: #297FCA;
					color: #fff;
				}
			}
		}
		.line1 {
			white-space: nowrap;
			.icon {
				margin-right: 16px;
			}
			.createdWhen {
				color: #575757;
				opacity: 0.8;
				font-weight: 300;
				font-size: 16px;
				line-height: 24px;
				vertical-align: middle;
			}
			.createdBy {
				color: #297FCA;
				font-weight: 500;
				margin-left: 16px;
				font-size: 14px;
				text-decoration: none !important;
			}
		}
		.line2 {
			padding-left: 36px;
			font-size: 16px;
			line-height: 36px;
			font-weight: 400;
			color: #575757;

			.more {
				color: #297FCA;
				margin-left: 8px;
				font-size: 80%;
				text-decoration: none;
				padding: 2px;
				border-radius: 4px;
			}
		}
	}
`;

const TextBlock = styled.div`
	white-space: pre-wrap;
	max-height: 220px;
	overflow-y: auto;
	padding: 4px;

	.diff_deleted {
		opacity: 0.6;
		text-decoration: line-through;
		color: #DC0000;
	}

	.diff_added {
//		font-weight: 500;
	}

	.diff_same {
		color: #575757;
	}
`;

export function getTransitionDescription(fromId, toId) {
	const fullId = 'submissionTransition.' + fromId + '2' + toId;
	const simpleId = 'submissionTransition.' + toId;
	let res = getPhrase(fullId);
	if (res === fullId) {
		res = getPhrase(simpleId);
		if (res === simpleId) {
			res = getLookup('submissionStatus').getText(toId) || toId;
		}
	}
	return res;
}

function getPrevStatusChange(transitions, trans) {
	const statusId = trans.get('toStatusId');
	let postTrans = false;
	for (let i=0; i<transitions.size; i++) {
		const curr = transitions.get(i);
		if (postTrans) {
			if (curr.get('toStatusId') === statusId) {
				return curr;
			}
		} else if (trans === curr) {
			postTrans = true;
		}
	}
	return;
}

function getFileIdSet(files) {
	let res = {};
	if (files) {
		files.forEach(file => {
			res[file.get('fileId')] = true;
		});
	}
	return res;
}

const getStatusChangeDiff = memoizeOne((n, o) => {
	let nFiles = n.get('files') || EMPTY_LIST;
	let oFiles = (o || EMPTY_MAP).get('files') || EMPTY_LIST;
	let nComments = n.get('comments') || '';
	let oComments = (o || EMPTY_MAP).get('comments') || '';
	let dmp = new DiffMatchPatch();
	let comments = dmp.diff_main(oComments, nComments);
	dmp.diff_cleanupSemantic(comments);

	const nFileIdSet = getFileIdSet(nFiles);
	const oFileIdSet = getFileIdSet(oFiles);

	let addedFiles = nFiles.filter(file => !oFileIdSet[file.get('fileId')]).map(file => file.set('_added', true));
	let sameFiles = nFiles.filter(file => oFileIdSet[file.get('fileId')]);
	let deletedFiles = oFiles.filter(file => !nFileIdSet[file.get('fileId')]).map(file => file.set('_deleted', true));
	return {
		comments,
		files: deletedFiles.concat(sameFiles).concat(addedFiles)
	};
});

export class StatusChangeHistory extends React.PureComponent {
	constructor() {
		super();
		this.state = {};
		this.closeDetail = this.closeDetail.bind(this);
		this.downloadFile = this.downloadFile.bind(this);
	}

	async downloadFile(file) {
		const uf = await webapi.execute('/api/submission/getAttachmentDownloadInfo', {
			transitionId: file.get('transitionId'),
			seqId: file.get('seqId')
		});
		if (uf === REPORTED_EXCEPTION) {
			return;
		}
		downloadFile(uf.fileId, uf.originalFileName);
	}

	closeDetail() {
		this.setState({detail: null});
	}

	renderTransitionDetail() {
		const detail = this.state.detail;
		if (!detail) {
			return null;
		}
		const comments = (detail.get('comments') || '').trim();
		const diff = getStatusChangeDiff(detail, this.state.prevDetail);
		const showDiffs = this.props.withDiffs && !!this.state.prevDetail;
		const showDeleted = true;
		const files = showDiffs ? diff.files : detail.get('files') || EMPTY_LIST;
		return (
			<Dialog minWidth="440px" onCloseRequested={this.closeDetail}>
				<DialogHeader>{getLookup('submissionStatus').getText(detail.get('toStatusId'))}</DialogHeader>
				{
					comments &&
						<DialogContent>
							<TextBlock>{
								showDiffs ?
									diff.comments.map((block, index) => {
										const type = block[0];
										if (!showDeleted && type < 0) {
											return null;
										}
										let value = block[1];
										if (type < 0 && !value.trim()) {
											return null;
										}
										return (
											<span key={index} className={'diff_' + (type > 0 ? 'added' : type < 0 ? 'deleted' : 'same')}>{value}</span>
										);
									}) :
									comments
							}</TextBlock>
						</DialogContent>

				}
				<FileList
					files={files}
					onDownloadRequested={this.downloadFile}
					maxHeight="50vh"
				/>
			</Dialog>
		);
	}

	render() {
		const transitions = this.props.transitions;
		if (!transitions || transitions.size === 0) {
			return null;
		}
		return (
			<StatusChangeHistoryWrapper>
				<div className="header">{getPhrase('changeHistory')}</div>
				{
					transitions.map((trans, index) => {
						const hasComments = !!(trans.get('comments') || '').trim();
						const hasFiles = (trans.get('files') || EMPTY_LIST).size > 0;
						const isActive = hasComments || hasFiles;
						return (
							<div
								className={'transition' + (isActive ? ' active' : '')}
								key={index}
								onMouseDown={e => {
									e.preventDefault();
									if (isActive) {
										this.setState({
											detail: trans,
											prevDetail: trans.get('toStatusId') === 'SUBMITTED' ? getPrevStatusChange(transitions, trans) || EMPTY_MAP : null
										});
									}
								}}
							>
								<div className="line1">
									<Icon
										name="clock"
									/>
									<span className="createdWhen">{DateUtils.formatDate(trans.get('createdWhen'))}</span>
									{
										this.props.withCreatedBy &&
											<span className="createdBy" title={trans.get('createdBy')}>{(trans.get('createdByDisplayName') || trans.get('createdBy') || '').replace('h$', '')}</span>

									}
								</div>
								<div className="line2">
									<span className="statusText">{getTransitionDescription(trans.get('fromStatusId'), trans.get('toStatusId'))}</span>
									{
										isActive &&
											<span className="more">{getPhrase('more')}..</span>

									}
								</div>
							</div>
						);
					})
				}
				{this.renderTransitionDetail()}
			</StatusChangeHistoryWrapper>
		);
	}
}
